// The team's events in a workspace window (GET /api/team/events, one stream of
// every workspace the person is in), kept beside Me's own stream.
//
// Promise:
// - The stream runs while the window shows a workspace (`ensure`), and stops
//   when it leaves for Me or signs out (`stop`).
// - Each event goes to every handler registered for its type: this part's
//   (workspace, members, roles, invites, spaces) and the sections'. A type no
//   one registered is ignored; one handler's mistake does not stop the others.
// - Handlers drop only the queries of the event's workspace (["ws", slug, ...]).
//   An event of a workspace not in the cached list refetches the list.
// - Like Me's stream: the first `ready` refetches what was fetched before it,
//   reconnects resume after the last cursor, and a refused cursor refetches
//   every team query and goes on from the head.
import type { QueryClient } from "@tanstack/react-query";
import type {
  TeamEventView,
  WorkspaceSummary,
  WorkspaceView,
} from "@trameria/cloud-api/workspace-shared";
import {
  type Api,
  type EventStream,
  openEventStream,
  type StreamedEvent,
} from "@trameria/ui-core";
import {
  invitesKey,
  membersKey,
  myCasesPrefix,
  rolesKey,
  spacePeoplePrefix,
  spacesKey,
  teamPrefix,
  workspacesKey,
  workspaceViewKey,
  wsPrefix,
} from "./queries";
import type {
  TeamRealtimeContext,
  TeamRealtimeHandler,
  TeamRealtimeHandlers,
} from "./sections";

export const TEAM_EVENTS_URL = "/api/team/events";

/** Every query of the workspace screens: under ["team"] and ["ws"]. */
const refetchAll = (qc: QueryClient) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: teamPrefix }),
    qc.invalidateQueries({ queryKey: ["ws"] }),
  ]);

/** A handler for one workspace's queries; an unknown workspace refetches the list. */
const inWorkspace =
  (
    fn: (
      slug: string,
      payload: Record<string, unknown>,
      ctx: TeamRealtimeContext,
    ) => unknown,
  ): TeamRealtimeHandler =>
  (payload, ctx) => {
    const slug = ctx.slugOf(ctx.event.workspaceId);
    if (slug === null) {
      void ctx.qc.invalidateQueries({ queryKey: workspacesKey });
      return;
    }
    void fn(slug, payload, ctx);
  };

const drop = (qc: QueryClient, ...keys: ReadonlyArray<readonly unknown[]>) =>
  Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));

const isMe = (
  qc: QueryClient,
  slug: string,
  payload: Record<string, unknown>,
) =>
  payload.userId !== undefined &&
  qc.getQueryData<WorkspaceView>(workspaceViewKey(slug))?.me.userId ===
    payload.userId;

/** This part's own handlers. */
export const workspaceTeamHandlers: TeamRealtimeHandlers = {
  "workspace.updated": inWorkspace((slug, _p, { qc }) =>
    drop(qc, workspacesKey, wsPrefix(slug)),
  ),
  "member.joined": inWorkspace((slug, _p, { qc }) =>
    drop(qc, membersKey(slug), rolesKey(slug)),
  ),
  "member.updated": inWorkspace((slug, payload, { qc }) =>
    isMe(qc, slug, payload)
      ? // My roles or kind changed: what I may do and see changes with them.
        drop(qc, wsPrefix(slug))
      : drop(qc, membersKey(slug), rolesKey(slug), spacePeoplePrefix(slug)),
  ),
  "member.left": inWorkspace((slug, payload, { qc }) =>
    isMe(qc, slug, payload)
      ? drop(qc, workspacesKey, wsPrefix(slug))
      : drop(qc, membersKey(slug), rolesKey(slug), spacePeoplePrefix(slug)),
  ),
  "role.changed": inWorkspace((slug, _p, { qc }) =>
    drop(qc, rolesKey(slug), membersKey(slug), workspaceViewKey(slug)),
  ),
  "invite.changed": inWorkspace((slug, _p, { qc }) =>
    drop(qc, invitesKey(slug)),
  ),
  "space.created": inWorkspace((slug, _p, { qc }) => drop(qc, spacesKey(slug))),
  "space.updated": inWorkspace((slug, _p, { qc }) =>
    drop(qc, spacesKey(slug), spacePeoplePrefix(slug)),
  ),
  "space.deleted": inWorkspace((slug, _p, { qc }) => drop(qc, spacesKey(slug))),
  "space.members_changed": inWorkspace((slug, _p, { qc }) =>
    drop(qc, spacesKey(slug), spacePeoplePrefix(slug)),
  ),
  "space.order_changed": inWorkspace((slug, _p, { qc }) =>
    drop(qc, spacesKey(slug)),
  ),
};

/** Me's events this part listens to: a Case changing may change my Cases chips. */
export const workspaceMeHandlers = {
  "case.changed": (_payload: unknown, { qc }: { qc: QueryClient }) =>
    void qc.invalidateQueries({ queryKey: myCasesPrefix }),
  "case.deleted": (_payload: unknown, { qc }: { qc: QueryClient }) =>
    void qc.invalidateQueries({ queryKey: myCasesPrefix }),
};

function teamEventOf(event: StreamedEvent): TeamEventView {
  const data = event.data;
  return {
    ...(event.cursor ? { cursor: event.cursor } : {}),
    workspaceId: typeof data.workspaceId === "string" ? data.workspaceId : "",
    spaceId: typeof data.spaceId === "string" ? data.spaceId : null,
    type: event.type,
    payload: (event.payload ?? {}) as Record<string, unknown>,
    at: event.at,
  };
}

export interface TeamStreamOptions {
  qc: QueryClient;
  api: Api;
  handlers: readonly TeamRealtimeHandlers[];
  url?: string;
  fetch?: typeof fetch;
  /** For tests: the stream to use instead of EventSource. */
  open?: typeof openEventStream;
  onError?(error: unknown, event: TeamEventView): void;
}

export function startTeamRealtime(options: TeamStreamOptions): EventStream {
  const { qc, api } = options;
  const byType = new Map<string, TeamRealtimeHandler[]>();
  for (const set of options.handlers)
    for (const [type, handler] of Object.entries(set))
      byType.set(type, [...(byType.get(type) ?? []), handler]);
  const slugOf = (workspaceId: string) =>
    qc
      .getQueryData<WorkspaceSummary[]>(workspacesKey)
      ?.find((w) => w.id === workspaceId)?.slug ?? null;
  let ready = false;
  return (options.open ?? openEventStream)({
    url: options.url ?? TEAM_EVENTS_URL,
    fetch: options.fetch,
    types: [...byType.keys()],
    onReady() {
      if (ready) return;
      ready = true;
      void refetchAll(qc);
    },
    onCursorRejected() {
      void refetchAll(qc);
    },
    onEvent(streamed) {
      const event = teamEventOf(streamed);
      for (const handler of byType.get(event.type) ?? [])
        try {
          handler(event.payload, { qc, api, event, slugOf });
        } catch (error) {
          options.onError?.(error, event);
        }
    },
  });
}

export interface TeamStream {
  /** Opens the stream unless it runs. */
  ensure(): void;
  stop(): void;
}

export function createTeamStream(options: TeamStreamOptions): TeamStream {
  let stream: EventStream | null = null;
  return {
    ensure() {
      if (!stream) stream = startTeamRealtime(options);
    },
    stop() {
      stream?.close();
      stream = null;
    },
  };
}
